import React, { useCallback, useEffect, useState } from 'react';
import BaseText from './BaseText';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSelector } from 'react-redux';
import { FontSize, CommonProperties, Color, CommonStyles } from '../styles';
import Images from '../constants/Images';
import { scale } from '../utils/scale.utils';
import Icon from './Icon';
import { getAirVendor, getCarVendor, getHotelVendor } from '../services/api/profile.service';
import { selectTripSummary } from '../store/selectors/search.selector';
import { useNavigation } from '@react-navigation/native';
import { Modals } from '../constants/ScreenNames';
import { PROGRAM_NUMBER, PassengerVendorCode, searchTypesEnum } from '../constants/Enums';
import FormError from './FormError';

export default function PassengerLoyalty({
  loyalty,
  id,
  passengerIndex,
  formProps,
  type,
  errorMessage,
}) {
  const navigation = useNavigation();
  const [selectList, setSelectList] = useState([]);
  const tripSummary = useSelector(selectTripSummary);
  const allAirLoyalties = tripSummary?.FrequentFlyers;
  const allHotelLoyalties = tripSummary?.HotelLoyaltys;
  const allCarLoyalties = tripSummary?.CarLoyaltys;
  const { AIR, HOTEL, CAR } = searchTypesEnum;

  const vendorNameMap = {
    [HOTEL]: loyalty?.HotelChainName,
    [AIR]: loyalty?.AirLineName,
    [CAR]: loyalty?.CarVendorName,
  };

  const propertyMap = {
    hotel: 'HotelChainCode',
    air: 'AirLineCode',
    car: 'CarVendorCode',
  };

  useEffect(() => {
    fetchVendors(type);
  }, []);
  const getVendorIcon = (code, type) => {
    if (type === AIR) {
      return code ? Images.air['36x36'][code] : null;
    } else if (type === HOTEL) {
      return code ? Images.hotel['24x24'][code] : null;
    } else if (type === CAR) {
      return code ? Images.car['50x20'][code] : null;
    }
  };

  //relies on selectList, formProps, passengerIndex, id, and type.
  const getSelectedLabel = useCallback((type, selectList, formProps, passengerIndex, id) => {
    let loyaltyArray = [];
    let loyaltyId = '';
    if (selectList?.length > 0) {
      if (type === AIR) {
        loyaltyArray = formProps?.values?.passengers[passengerIndex]?.FrequentFlyers;
        loyaltyId = PassengerVendorCode.AIR;
      } else if (type === HOTEL) {
        loyaltyArray = formProps?.values?.passengers[passengerIndex]?.HotelLoyalty;
        loyaltyId = PassengerVendorCode.HOTEL;
      } else if (type === CAR) {
        loyaltyArray = formProps?.values?.passengers[passengerIndex]?.CarLoyalty;
        loyaltyId = PassengerVendorCode.CAR;
      }

      const selectedLoyalty = loyaltyArray[id]?.[loyaltyId];

      const selectedOne = selectList?.filter((f) => f.value === selectedLoyalty)?.[0]?.label || '';

      if (selectedOne.includes('-')) {
        return [`${selectedOne.split(' ')[0]} ${selectedOne.split(' ')[1]}`, loyaltyArray];
      } else {
        return [selectedOne, loyaltyArray];
      }
    }
    return [loyaltyId, loyaltyArray];
  }, []);

  const combineAndAddProgramNoToDropdownLabel = (selectList, allLoyalties, type) => {
    const result = [];

    if (type === HOTEL) {
      if (allLoyalties !== undefined || allLoyalties.length > 0) {
        for (const hotel of selectList) {
          const matchingHotelLoyalty = allLoyalties.find(
            (hotelLoyalty) =>
              hotelLoyalty.HotelChainCode === hotel.value && hotelLoyalty.ProgramNumber !== ''
          );

          hotel.label = hotel.label.split(/[\s,\t,\n]+/).join(' ');
          if (matchingHotelLoyalty) {
            const label = `${hotel.label} - ${matchingHotelLoyalty.ProgramNumber}`;
            result.push({
              label,
              value: hotel.value,
            });
          } else {
            result.push(hotel);
          }
        }
      }
    } else if (type === AIR) {
      if (allLoyalties !== undefined || allLoyalties.length > 0) {
        for (const airline of selectList) {
          const matchingFrequentFlyer = allLoyalties.find(
            (frequentFlyer) =>
              frequentFlyer.AirLineCode === airline.value && frequentFlyer.ProgramNo !== ''
          );

          if (matchingFrequentFlyer) {
            const label = `${airline.label} - ${matchingFrequentFlyer.ProgramNo}`;
            result.push({
              label,
              value: airline.value,
            });
          } else {
            result.push(airline);
          }
        }
      }
    } else if (type === CAR) {
      if (allLoyalties !== undefined || allLoyalties.length > 0) {
        for (const carVendor of selectList) {
          const matchingCarLoyalty = allLoyalties.find(
            (carLoyalty) =>
              carLoyalty.CarVendorCode === carVendor.value && carLoyalty.ProgramNumber !== ''
          );

          if (matchingCarLoyalty) {
            const label = `${carVendor.label} - ${matchingCarLoyalty.ProgramNumber}`;
            result.push({
              label,
              value: carVendor.value,
            });
          } else {
            result.push(carVendor);
          }
        }
      }
    }

    setSelectList(result);
  };

  const onPressHandler = () => {
    navigation.navigate(Modals.PassengerLoyaltyForm, {
      id: id,
      passengerIndex: passengerIndex,
      formProps: formProps,
      selectList: selectList,
      type: type,
    });
  };
  const fetchVendors = async (type) => {
    if (type === AIR) {
      const data = await getAirVendor();
      const allAirlines = data?.map((i) => ({ label: i.Name, value: i.Code }));
      combineAndAddProgramNoToDropdownLabel(allAirlines, allAirLoyalties, AIR);
    } else if (type === HOTEL) {
      const data = await getHotelVendor();
      const allHotels = data?.map((i) => ({ label: i.HotelChainName, value: i.HotelChainCode }));
      combineAndAddProgramNoToDropdownLabel(allHotels, allHotelLoyalties, HOTEL);
    } else if (type === CAR) {
      const data = await getCarVendor();
      const allCars = data?.map((i) => ({ label: i.VendorName, value: i.VendorCode }));
      combineAndAddProgramNoToDropdownLabel(allCars, allCarLoyalties, CAR);
    }
  };

  const [selectedOne, loyaltyArray] = getSelectedLabel(
    type,
    selectList,
    formProps,
    passengerIndex,
    id
  );

  const programNumberKey = type === AIR ? PROGRAM_NUMBER.AIR : PROGRAM_NUMBER.OTHERS; //hotel,car is ProgramNumber
  const vendorImage = getVendorIcon(loyalty?.[propertyMap[type]], type) || null;
  const vendorName = vendorNameMap[type] || '';
  const info = {
    icon: vendorImage ? vendorImage : Images.defaultImage,
    label: selectedOne?.replace(/\s{2,}/g, ' ').trim() || '',
    small: loyaltyArray[id]?.[programNumberKey] || 'Add loyalty number',
  };
  return (
    <>
      <View style={styles.container}>
        <View style={styles.subTitleText}>
          <BaseText numberOfLines={2}>{vendorName}</BaseText>
        </View>
        <View style={styles.itemContainer}>
          <Image
            style={styles.image}
            source={info.icon ? info.icon : Images.defaultImage}
            resizeMode="contain"
          />
          <View style={styles.textGroup}>
            <View style={styles.textContainer}>
              <BaseText numberOfLines={1} style={CommonStyles.body}>
                {info.label}
              </BaseText>
              <BaseText style={CommonStyles.small}>{info.small}</BaseText>
            </View>
            <TouchableOpacity onPress={onPressHandler}>
              <Icon name="edit" size={FontSize.body} color={Color.text.high} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
      {errorMessage && (
        <View style={[CommonProperties.mt2, CommonProperties.mb3]}>
          <FormError errorMessage={errorMessage} />
        </View>
      )}
    </>
  );
}

// Styles and constants

const styles = StyleSheet.create({
  container: { flex: 1, flexDirection: 'row', ...CommonProperties.pt2 },
  itemContainer: {
    flex: 2, // Take twice the available space as compared to subTitleText
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Color.accent,
    ...CommonProperties.p3,
    ...CommonProperties.borderRadius,
    ...CommonProperties.shadow,
  },
  image: {
    height: scale(36),
    width: scale(36),
    ...CommonProperties.mr3,
  },
  textGroup: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  textContainer: {
    width: 150, // Set the width of the text container
  },
  subTitleText: {
    flex: 1, // Let it take available vertical space
    justifyContent: 'center', // Vertically center the text
    ...CommonProperties.pr3,
  },
});
